import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles, getArticleBySlug } from "@/mocks/articles";
import { useCurrentLanguage, localize } from "@/hooks/useLanguage";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const lang = useCurrentLanguage();
  const article = getArticleBySlug(slug ?? "");

  useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo(0, 0);
    html.style.scrollBehavior = prev;
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="px-4 md:px-6 pt-32 pb-20 text-center">
          <h1 className="font-heading font-bold text-2xl text-foreground-950">Article not found</h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-md bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors"
          >
            {t("article.back")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 2);

  const siteUrl = (import.meta.env.VITE_SITE_URL || "").replace(/\/+$/, "");
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: localize(article.title, lang),
    description: localize(article.excerpt, lang),
    image: article.image,
    datePublished: article.updated,
    dateModified: article.updated,
    inLanguage: lang,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/article/${article.slug}`,
    },
    author: {
      "@type": "Organization",
      name: "USFans Link",
    },
    publisher: {
      "@type": "Organization",
      name: "USFans Link",
      logo: {
        "@type": "ImageObject",
        url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2277e9ffbdee9fd651886f1aeeba0d56.jpeg",
      },
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <article>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c") }}
          />
          <header className="relative w-full">
            <div className="absolute inset-0">
              <img
                src={article.image}
                alt={localize(article.title, lang)}
                title={`${localize(article.title, lang)} - USFans Spreadsheet`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/60"></div>
            </div>
            <div className="relative z-10 w-full px-4 md:px-6 pt-32 md:pt-40 pb-16 md:pb-20">
              <div className="max-w-3xl mx-auto">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm text-background-100 hover:text-background-50 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t("article.back")}
                </Link>
                <h1 className="mt-5 font-heading font-extrabold text-2xl md:text-4xl text-background-50 leading-tight">
                  {localize(article.title, lang)}
                </h1>
                <div className="mt-5 flex items-center gap-4 text-sm text-background-100">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime} {t("articles.minRead")}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {t("article.updated")}: {article.updated}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <div className="w-full px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-3xl mx-auto">
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                {localize(article.excerpt, lang)}
              </p>

              <div className="mt-10 space-y-10">
                {article.sections.map((section, idx) => (
                  <section key={idx}>
                    <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                      {localize(section.heading, lang)}
                    </h2>
                    {section.paragraphs.map((p, pIdx) => (
                      <p key={pIdx} className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                        {localize(p, lang)}
                      </p>
                    ))}
                  </section>
                ))}
              </div>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-6">
                  <h3 className="flex items-center gap-2 font-heading font-semibold text-base text-primary-800">
                    <i className="ri-checkbox-circle-line text-primary-600"></i>
                    {t("article.pros")}
                  </h3>
                  <ul className="mt-4 space-y-2.5">
                    {article.pros.map((pro, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-check-line text-primary-600 mt-0.5 shrink-0"></i>
                        <span>{localize(pro, lang)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-accent-50 border border-accent-200 rounded-lg p-6">
                  <h3 className="flex items-center gap-2 font-heading font-semibold text-base text-accent-800">
                    <i className="ri-error-warning-line text-accent-600"></i>
                    {t("article.cons")}
                  </h3>
                  <ul className="mt-4 space-y-2.5">
                    {article.cons.map((con, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-close-line text-accent-600 mt-0.5 shrink-0"></i>
                        <span>{localize(con, lang)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-12">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  {t("article.faq")}
                </h2>
                <div className="mt-5 space-y-3">
                  {article.faq.map((faq, idx) => (
                    <div key={idx} className="bg-background-100 rounded-lg border border-background-200/70 p-5">
                      <h3 className="font-medium text-sm md:text-base text-foreground-900">
                        {localize(faq.question, lang)}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                        {localize(faq.answer, lang)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg p-8 text-center">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-background-50">
                  {t("article.ctaTitle")}
                </h2>
                <p className="mt-2 text-sm text-background-100">{t("article.ctaText")}</p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap px-7 py-3 rounded-md bg-background-50 text-primary-700 font-bold text-sm hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  {t("article.ctaButton")}
                </a>
              </div>

              <div className="mt-12">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
                  {t("article.related")}
                </h2>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {related.map((r) => (
                    <Link
                      key={r.slug}
                      to={`/article/${r.slug}`}
                      className="group bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer"
                    >
                      <div className="w-full h-36 overflow-hidden">
                        <img
                          src={r.image}
                          alt={localize(r.title, lang)}
                          title={`${localize(r.title, lang)} - USFans Spreadsheet`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading font-semibold text-sm text-foreground-900 group-hover:text-primary-700 transition-colors leading-snug">
                          {localize(r.title, lang)}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}